import React, { memo, useContext, useId } from 'react'

import {
  Button, Icon, Slider, Undo, Redo, Eraser, Brush, RemoveBackground, ResizeUpscale,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number } from 'prop-types'

import { iconSize, BRUSH } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import { NOOP } from 'Utils/stableDefaults'

import ToolTooltip from './Components/ToolTooltip/ToolTooltip'

const undoIcon = <Icon svgComponent={Undo} size={iconSize} color="dark" notSpaced />
const redoIcon = <Icon svgComponent={Redo} size={iconSize} color="dark" notSpaced />
const eraserIcon = <Icon svgComponent={Eraser} size={iconSize} color="dark" notSpaced />
const brushIcon = <Icon svgComponent={Brush} size={iconSize} color="dark" notSpaced />
const removeBackgroundIcon = <Icon svgComponent={RemoveBackground} size={iconSize} color="dark" notSpaced />
const upscaleIcon = <Icon svgComponent={ResizeUpscale} size={iconSize} color="dark" notSpaced />

// Drawing tools for the Edit canvas, rendered into the composer's action row. Eraser
// and brush are a segmented pair rather than one toggle: each selects its own mode, so
// clicking the mode already in use is a no-op instead of flipping to the other tool.
function Toolbar({
  canUndo,
  canRedo,
  isErasing,
  brushSize,
  isSending,
  isImageToolDisabled = false,
  onUndo,
  onRedo,
  onErasingChange,
  onBrushSizeChange,
  onRemoveBackground = NOOP,
  onUpscale = NOOP,
}) {
  const t = useTranslation()
  const { twoDImagesIdeationFlowEnabled, unfinishedTwoDFeaturesEnabled } = useContext(ThreeDStudioPageContext)
  const hasImageTools = twoDImagesIdeationFlowEnabled && unfinishedTwoDFeaturesEnabled
  // EditTab and FullImageEditor each render a Toolbar, so tooltip ids need a per-instance prefix.
  const tooltipIdPrefix = `edit-toolbar-${useId().replace(/:/g, '')}`
  const undoLabel = t('imageAssistantPanel.editTab.toolbar.undo')
  const redoLabel = t('imageAssistantPanel.editTab.toolbar.redo')
  const removeBackgroundLabel = t('imageAssistantPanel.editTab.toolbar.removeBackground')
  const upscaleLabel = t('imageAssistantPanel.editTab.toolbar.upscale')
  const eraserLabel = t('imageAssistantPanel.editTab.toolbar.eraser')
  const brushLabel = t('imageAssistantPanel.editTab.toolbar.brush')
  const handleSelectEraser = () => onErasingChange(true)
  const handleSelectBrush = () => onErasingChange(false)

  return (
    <div className="image-assistant-edit-tab__tools">
      <ToolTooltip id={`${tooltipIdPrefix}-undo`} label={undoLabel}>
        <Button
          variant="only-icon"
          type="secondary"
          size="small"
          actionType="button"
          customClass="image-assistant-edit-tab__tool"
          icon={undoIcon}
          aria-label={undoLabel}
          disabled={!canUndo || isSending}
          onClick={onUndo}
        />
      </ToolTooltip>
      <ToolTooltip id={`${tooltipIdPrefix}-redo`} label={redoLabel}>
        <Button
          variant="only-icon"
          type="secondary"
          size="small"
          actionType="button"
          customClass="image-assistant-edit-tab__tool"
          icon={redoIcon}
          aria-label={redoLabel}
          disabled={!canRedo || isSending}
          onClick={onRedo}
        />
      </ToolTooltip>
      {hasImageTools && (
        <>
          <ToolTooltip id={`${tooltipIdPrefix}-remove-background`} label={removeBackgroundLabel}>
            <Button
              variant="only-icon"
              type="secondary"
              size="small"
              actionType="button"
              customClass="image-assistant-edit-tab__tool"
              icon={removeBackgroundIcon}
              aria-label={removeBackgroundLabel}
              disabled={isSending || isImageToolDisabled}
              onClick={onRemoveBackground}
            />
          </ToolTooltip>
          <ToolTooltip id={`${tooltipIdPrefix}-upscale`} label={upscaleLabel}>
            <Button
              variant="only-icon"
              type="secondary"
              size="small"
              actionType="button"
              customClass="image-assistant-edit-tab__tool"
              icon={upscaleIcon}
              aria-label={upscaleLabel}
              disabled={isSending || isImageToolDisabled}
              onClick={onUpscale}
            />
          </ToolTooltip>
        </>
      )}
      <div className="image-assistant-edit-tab__brush">
        <ToolTooltip id={`${tooltipIdPrefix}-eraser`} label={eraserLabel}>
          <Button
            variant="only-icon"
            type="secondary"
            size="small"
            actionType="button"
            customClass="image-assistant-edit-tab__mode"
            icon={eraserIcon}
            aria-label={eraserLabel}
            aria-pressed={isErasing}
            disabled={isSending}
            onClick={handleSelectEraser}
          />
        </ToolTooltip>
        <ToolTooltip id={`${tooltipIdPrefix}-brush`} label={brushLabel}>
          <Button
            variant="only-icon"
            type="secondary"
            size="small"
            actionType="button"
            customClass="image-assistant-edit-tab__mode"
            icon={brushIcon}
            aria-label={brushLabel}
            aria-pressed={!isErasing}
            disabled={isSending}
            onClick={handleSelectBrush}
          />
        </ToolTooltip>
        <Slider
          hideRangeValues
          min={BRUSH.MIN}
          max={BRUSH.MAX}
          step={BRUSH.STEP}
          value={brushSize}
          onChange={onBrushSizeChange}
          isDisabled={isSending}
          wrapperClassName="image-assistant-edit-tab__brush-slider"
        />
      </div>
    </div>
  )
}

Toolbar.propTypes = {
  canUndo: bool.isRequired,
  canRedo: bool.isRequired,
  isErasing: bool.isRequired,
  brushSize: number.isRequired,
  isSending: bool.isRequired,
  isImageToolDisabled: bool,
  onUndo: func.isRequired,
  onRedo: func.isRequired,
  onErasingChange: func.isRequired,
  onBrushSizeChange: func.isRequired,
  onRemoveBackground: func,
  onUpscale: func,
}

export default memo(Toolbar)
